import { Injectable } from '@angular/core';
import { ApiService, HttpMethod } from '../common/api.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Album } from './album.model';
import { AlbumEditModel } from './album-edit.model';
import { DeletedAlbum } from './deleted-album.model';
import { ImageAsset } from '../images/image-asset-model';
import { MyCampApp } from '../common/enumerations';
import { UploadFileResult } from '../images/image-manager.service';

@Injectable({
    providedIn: 'root'
})
export class AlbumApiService extends ApiService
{
    constructor(protected override http: HttpClient)
    {
        super(http);
    }

    getAlbum(id: number): Observable<Album>
    {
        return this.apiRequest(this.getAlbumsUrl(id), HttpMethod.Get).pipe(map((json: any) => new Album(json)));
    }

    createAlbum(model: AlbumEditModel): Observable<Album>
    {
        return this.apiRequest<any>(this.createAlbumUrl(), HttpMethod.Post, JSON.stringify(model))
            .pipe(map((json: any) => new Album(json)));
    }

    updateAlbum(model: AlbumEditModel)
    {
        return this.apiRequest<any>(this.updateOrDeleteAlbumUrl(model.Id), HttpMethod.Put, JSON.stringify(model))
            .pipe(map((json: any) => new Album(json)));
    }

    moveAlbum(albumId: number, fromParentId: number, fromChildAlbumIds: number[], toParentId: number, toChildAlbumIds: number[]): Observable<Album>
    {
        return this.apiRequest<any>(this.moveAlbumUrl(), HttpMethod.Put, JSON.stringify({
            AlbumId: albumId,
            FromParentId: fromParentId,
            FromParentChildAlbumIds: fromChildAlbumIds,
            ToParentId: toParentId,
            ToParentChildAlbumIds: toChildAlbumIds,
        })).pipe(map((json: any) => new Album(json)));
    }

    addMediaToAlbum(id: number, type: MyCampApp.AssetType, uploadResult: UploadFileResult)
    {
        return this.apiRequest<any>(this.addMediaToAlbumUrl(id), HttpMethod.Post, JSON.stringify({
            AssetType: type,
            Url: uploadResult.fileOrUrl,
            width: uploadResult.width,
            height: uploadResult.height
        })).pipe(map((photo: ImageAsset) => new ImageAsset(photo)));
    }

    deleteMediaFromAlbum(id: number, imageAssetIds: number[])
    {
        return this.apiRequest<any>(this.deleteMediaFromAlbumUrl(id), HttpMethod.Delete, JSON.stringify({
            ImageAssetIds: imageAssetIds
        }));
    }

    moveMediaToAlbum(fromId: number, toId: number, imageAssetIds: number[])
    {
        return this.apiRequest<any>(this.moveMediaToAlbumUrl(), HttpMethod.Put, JSON.stringify({
            FromAlbumId: fromId,
            ToAlbumId: toId,
            ImageAssetIds: imageAssetIds
        }));
    }

    deleteAlbum(album: Album)
    {
        return this.apiRequest<any>(this.updateOrDeleteAlbumUrl(album.Id), HttpMethod.Delete);
    }

    getDeletedAlbums(): Observable<DeletedAlbum[]>
    {
        return this.apiRequest<any>(this.deletedAlbumsUrl(), HttpMethod.Get)
            .pipe(map((json: any[]) => json.map(item => new DeletedAlbum(item))));
    }

    restoreAlbums(albumIds: number[]): Observable<any>
    {
        return this.apiRequest<any>(this.restoreAlbumsUrl(), HttpMethod.Post, JSON.stringify({ AlbumIds: albumIds }));
    }

    duplicateAlbum(album: Album, newAlbumName: string): Observable<Album>
    {
        return this.apiRequest<any>(this.duplicateAlbumUrl, HttpMethod.Post, `{ "Id": ${album.Id}, "Name": "${newAlbumName}" } `)
            .pipe(map((json: any) => new Album(json)));
    }

    setImageForAlbum(id: number, url: string): Observable<ImageAsset>
    {
        return this.apiRequest<any>(this.setOrDeleteAlbumImageUrl(id), HttpMethod.Post, '{ "Url": "' + url + '"}')
        .pipe(map((json: any) => new ImageAsset(json)));
    }

    deleteImageForAlbum(id: number)
    {
        return this.apiRequest<any>(this.setOrDeleteAlbumImageUrl(id), HttpMethod.Delete);
    }

    // =========================================================================================================================================================
    // URLs
    // =========================================================================================================================================================
    /* tslint:disable:member-ordering */
    private get baseAlbumsApiUrl(): string { return `${this.baseApiUrl}albums`; }
    getAlbumsUrl(id: number): string { return `${this.baseAlbumsApiUrl}/${id}`; }
    createAlbumUrl() { return this.baseAlbumsApiUrl; }
    updateOrDeleteAlbumUrl(id: number) { return `${this.baseAlbumsApiUrl}/${id}`; }
    get duplicateAlbumUrl() { return `${this.baseAlbumsApiUrl}/duplicate`; }
    setOrDeleteAlbumImageUrl(id: number) { return `${this.baseAlbumsApiUrl}/${id}/thumbnail`; }
    moveAlbumUrl() { return `${this.baseAlbumsApiUrl}/moveAlbum`; }
    addMediaToAlbumUrl(id: number) { return `${this.baseAlbumsApiUrl}/${id}/addMedia`; }
    deleteMediaFromAlbumUrl(id: number) { return `${this.baseAlbumsApiUrl}/${id}/deleteMedia`; }
    moveMediaToAlbumUrl() { return `${this.baseAlbumsApiUrl}/moveMedia`; }
    deletedAlbumsUrl() { return `${this.baseAlbumsApiUrl}/deleted`; }
    restoreAlbumsUrl() { return `${this.baseAlbumsApiUrl}/restore`; }
}
